<script lang="ts" module>
  // Found is a search result from the server's geocoder (spec 003 FR-002).
  export interface Found {
    name: string;
    label: string;
    lat: number;
    lon: number;
    // source is local for the server's own map data, online for the
    // online address service (spec 009).
    source: 'local' | 'online';
  }
</script>

<script lang="ts">
  import { api } from '../../api/client';
  import { t } from '../../i18n/index.ts';

  // PlaceSearch finds a place by name or address through the server, near
  // where the map shows, when the member submits the search; the geocoder's
  // policy rules out searching on every keystroke. The server searches its
  // own map data; the online service is asked only when the member chooses
  // Search online (spec 009 FR-028).
  let { near, onPick }: { near: () => { lat: number; lon: number } | null; onPick: (f: Found) => void } = $props();

  let text = $state('');
  let results: Found[] | null = $state(null);
  let busy = $state(false);
  let error = $state('');
  // searched is the text the results are for; online is set once the
  // online service was asked for it.
  let searched = $state('');
  let online = $state(false);
  let onlineError = $state('');

  function query(q: string, askOnline: boolean): string {
    const at = near();
    const params = new URLSearchParams({ q });
    if (at) {
      params.set('lat', at.lat.toFixed(5));
      params.set('lon', at.lon.toFixed(5));
    }
    if (askOnline) params.set('online', 'true');
    return `/geocode/search?${params}`;
  }

  async function search(e: SubmitEvent) {
    e.preventDefault();
    const q = text.trim();
    if (q.length < 2) return;
    busy = true;
    error = '';
    onlineError = '';
    online = false;
    try {
      results = await api.get<Found[]>(query(q, false));
      searched = q;
    } catch (err: any) {
      error = err.message;
      results = null;
    } finally {
      busy = false;
    }
  }

  async function searchOnline() {
    busy = true;
    onlineError = '';
    try {
      const more = await api.get<Found[]>(query(searched, true));
      const have = new Set((results ?? []).map((f) => f.label));
      results = [...(results ?? []), ...more.filter((f) => !have.has(f.label))];
      online = true;
    } catch (err: any) {
      onlineError = err.message;
    } finally {
      busy = false;
    }
  }

  function pick(f: Found) {
    results = null;
    onPick(f);
  }
</script>

<form class="space-y-2" onsubmit={search}>
  <div class="flex gap-2">
    <input class="input flex-1 min-w-0" type="search" placeholder={t('componentsPlaceSearchPlaceholder')} aria-label={t('componentsPlaceSearchPlaceholder')}
           maxlength="200" bind:value={text} />
    <button class="btn btn-secondary" type="submit" disabled={busy || text.trim().length < 2} aria-label={t('componentsPlaceSearchButton')}>
      <i aria-hidden="true" class="las la-search"></i>
    </button>
  </div>
  {#if error}
    <p class="text-sm" style="color: var(--theme-error);">{error}</p>
  {:else if results}
    {#if results.length === 0}
      <p class="text-sm text-text-muted">
        {online ? t('componentsPlaceSearchNoneOnline') : t('componentsPlaceSearchNoneLocal')}
      </p>
    {:else}
      <ul class="found">
        {#each results as f (`${f.lat},${f.lon},${f.label}`)}
          <li>
            <button type="button" class="found-item" onclick={() => pick(f)}>
              <i aria-hidden="true" class="las {f.source === 'online' ? 'la-globe' : 'la-map-marker-alt'}"></i>
              <span class="truncate">{f.label}</span>
            </button>
          </li>
        {/each}
      </ul>
    {/if}
    {#if !online}
      <button type="button" class="btn btn-secondary w-full" onclick={searchOnline} disabled={busy}>
        <i aria-hidden="true" class="las la-globe"></i> {t('componentsPlaceSearchOnline')}
      </button>
      <p class="text-xs text-text-muted">{t('componentsPlaceSearchOnlineNote')}</p>
    {:else if results.length > 0 && !results.some((f) => f.source === 'online')}
      <p class="text-sm text-text-muted">{t('componentsPlaceSearchNoMoreOnline')}</p>
    {/if}
    {#if onlineError}
      <p class="text-sm" style="color: var(--theme-error);">{onlineError}</p>
    {/if}
  {/if}
</form>

<style>
  .found {
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-md);
    overflow: hidden;
  }
  .found-item {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.65rem;
    text-align: left;
    font-size: 0.875rem;
    background: none;
    border: 0;
    color: var(--theme-text);
    cursor: pointer;
  }
  .found li + li .found-item {
    border-top: 1px solid var(--theme-border);
  }
  .found-item:hover {
    background: var(--theme-surface0);
  }
</style>
